import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useLanguage } from '../../../contexts/LanguageContext';

const DocumentEditModal = ({ document, isOpen, onClose, onSave, sites, owners }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    siteId: '',
    owner: '',
    issueDate: '',
    expiryDate: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (document) {
      setFormData({
        title: document?.title || '',
        category: document?.category || '',
        siteId: document?.siteId || '',
        owner: document?.owner || '',
        issueDate: document?.issueDate || '',
        expiryDate: document?.expiryDate || '',
        notes: document?.notes || ''
      });
      setErrors({});
    }
  }, [document]);

  const categoryOptions = [
    { value: 'rams', label: 'H&S Docs' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'training', label: 'Training Certificates' },
    { value: 'plant', label: 'Plant Certificates' }
  ];

  const siteOptions = sites?.map(site => ({
    value: site?.id,
    label: site?.site_name
  }));

  const ownerOptions = owners?.map(owner => ({
    value: owner,
    label: owner
  }));

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Document title is required';
    }

    if (!formData?.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData?.siteId) {
      newErrors.siteId = 'Site is required';
    }

    if (!formData?.owner) {
      newErrors.owner = 'Owner is required';
    }

    if (!formData?.issueDate) {
      newErrors.issueDate = 'Issue date is required';
    }

    if (!formData?.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    }

    if (formData?.issueDate && formData?.expiryDate) {
      const issue = new Date(formData.issueDate);
      const expiry = new Date(formData.expiryDate);
      if (expiry <= issue) {
        newErrors.expiryDate = 'Expiry date must be after issue date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave({ ...document, ...formData });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
              <Icon name="Edit" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('editDocument')}</h2>
              <p className="text-sm text-muted-foreground">{t('updateDocumentDetails')}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label={t('closeModal')}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-4">
            <Input
              label={t('documentTitle')}
              type="text"
              placeholder={t('enterDocumentTitle')}
              value={formData?.title}
              onChange={(e) => handleChange('title', e?.target?.value)}
              error={errors?.title}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label={t('category')}
                options={categoryOptions}
                value={formData?.category}
                onChange={(value) => handleChange('category', value)}
                error={errors?.category}
                required
              />

              <Select
                label={t('site')}
                options={siteOptions}
                value={formData?.siteId}
                onChange={(value) => handleChange('siteId', value)}
                error={errors?.siteId}
                required
              />
            </div>

            <Select
              label={t('owner')}
              options={ownerOptions}
              value={formData?.owner}
              onChange={(value) => handleChange('owner', value)}
              error={errors?.owner}
              required
              searchable
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('issueDate')}
                type="date"
                value={formData?.issueDate}
                onChange={(e) => handleChange('issueDate', e?.target?.value)}
                error={errors?.issueDate}
                required
              />

              <Input
                label={t('expiryDate')}
                type="date"
                value={formData?.expiryDate}
                onChange={(e) => handleChange('expiryDate', e?.target?.value)}
                error={errors?.expiryDate}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('notes')}
              </label>
              <textarea
                value={formData?.notes}
                onChange={(e) => handleChange('notes', e?.target?.value)}
                placeholder={t('additionalNotesAboutDocument')}
                rows={4}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth resize-none"
              />
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            iconName="Save"
            iconPosition="left"
          >
            {t('saveChanges')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentEditModal;